import { hotjar } from 'react-hotjar';
import { useCallback, useEffect, useRef, useState } from 'react';
import { whenIdle } from '@/utils/whenIdle';

/**
 * Hotjar, initialised off the critical path.
 *
 * `hotjar.initialize` injects `static.hotjar.com/c/hotjar-<id>.js`, which Sentry
 * measures at p50 121ms / p95 1,178ms on `/jobs/:jobId` — roughly 279ms of
 * script time per page load, none of it needed before paint.
 *
 * Deferring it opens a race the old code did not have: `currUser` resolves from
 * `/me` asynchronously, so `setHotjarUser` can be called before `initialize` has
 * run. `hotjar.identify` is a no-op until then and was never retried, which
 * would have cost every recording its user attribution. The pending identify is
 * therefore held and flushed once init lands.
 */
export const useHotjar = () => {
  const pendingUserRef = useRef(null);
  const [isInitialised, setIsInitialised] = useState(false);

  useEffect(() => {
    if (!import.meta.env.VITE_HOTJAR_ID) {
      return undefined;
    }

    return whenIdle(() => {
      hotjar.initialize(import.meta.env.VITE_HOTJAR_ID, 6);
      setIsInitialised(true);
    });
  }, []);

  // Memoised: `PrivateRoutes` lists this in an effect's deps, so a fresh
  // identity each render re-ran that effect on every render.
  const setHotjarUser = useCallback(({ id, name, account }) => {
    if (!import.meta.env.VITE_HOTJAR_ID) {
      return;
    }

    if (hotjar.initialized()) {
      hotjar.identify(id, { account: account, userName: name });
      pendingUserRef.current = null;
      return;
    }

    pendingUserRef.current = { id, name, account };
  }, []);

  useEffect(() => {
    if (!isInitialised) return;

    const pending = pendingUserRef.current;
    if (!pending || !hotjar.initialized()) return;

    hotjar.identify(pending.id, {
      account: pending.account,
      userName: pending.name,
    });
    pendingUserRef.current = null;
  }, [isInitialised]);

  return { setHotjarUser };
};
